import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 2: Language of Coherence
 * Two overlapping torus shapes forming Vesica Piscis
 */
export default function VesicaPiscis({ audioData, isActive, position = [0, 0, 0] }) {
  const torus1Ref = useRef()
  const torus2Ref = useRef()
  const intersectionRef = useRef()

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    if (torus1Ref.current) {
      // Rotate first torus clockwise
      torus1Ref.current.rotation.y += delta * 0.12 + amplitude * 0.02
      torus1Ref.current.rotation.x = Math.sin(time * 0.5) * 0.3

      // Pulse with amplitude
      const scale = 1 + amplitude * 0.2
      torus1Ref.current.scale.setScalar(scale)
    }

    if (torus2Ref.current) {
      // Rotate second torus counter-clockwise
      torus2Ref.current.rotation.y -= delta * 0.12 + amplitude * 0.02
      torus2Ref.current.rotation.x = Math.cos(time * 0.5) * 0.3

      // Pulse with amplitude
      const scale = 1 + amplitude * 0.2
      torus2Ref.current.scale.setScalar(scale)
    }

    if (intersectionRef.current) {
      // Pulsing glow at intersection
      const glowIntensity = 2 + amplitude * 3 + Math.sin(time * 0.8) * 0.5
      intersectionRef.current.material.emissiveIntensity = glowIntensity

      // Beat pulse
      if (beat > 0.5) {
        intersectionRef.current.scale.setScalar(1.3)
      } else {
        intersectionRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), delta * 5)
      }
    }
  })

  return (
    <group position={position}>
      {/* First Torus - positioned left */}
      <mesh ref={torus1Ref} position={[-0.8, 0, 0]}>
        <torusGeometry args={[1.5, 0.4, 16, 100]} />
        <meshStandardMaterial
          color="#4169E1"
          transparent
          opacity={0.6}
          emissive="#4169E1"
          emissiveIntensity={0.5}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Second Torus - positioned right */}
      <mesh ref={torus2Ref} position={[0.8, 0, 0]}>
        <torusGeometry args={[1.5, 0.4, 16, 100]} />
        <meshStandardMaterial
          color="#9370DB"
          transparent
          opacity={0.6}
          emissive="#9370DB"
          emissiveIntensity={0.5}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Glowing intersection sphere */}
      <mesh ref={intersectionRef} position={[0, 0, 0]}>
        <sphereGeometry args={[0.6, 32, 32]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#FFD700"
          emissiveIntensity={2}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Point light at center */}
      <pointLight position={[0, 0, 0]} intensity={3} color="#FFD700" distance={8} />
    </group>
  )
}
